import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getCommercialODLoansBySearch,
  getWCTLoansBySearch,
} from "../../actions/loan.action";
import Pagination from "../../components/pagination";
import Table, { TableColumns } from "../../components/table";
import { CommercialLoanDetails, WCTLoanDetail } from "../../dto";
import { ReactComponent as Loader } from "../../images/loader.svg";
import dispatch from "../../middleware";
import { LoanStatusEnum, RoutePaths } from "../../models";
import { ReduxState } from "../../reducers";
import {
  clearFormData,
  dateToDDMMYYYY,
  isLoadingActive,
  splitKeyWithSpace,
} from "../../utils";
import { LoanIdLink } from "./LoanIdLink";
import { LoanStatusBadge } from "./LoanStatusBadge";
import { EnterpriseLoanPageSearchQuery } from "./types";

interface LoanListContainerProps {
  tab: EnterpriseLoanPageSearchQuery["tab"];
  queryState: EnterpriseLoanPageSearchQuery;
  onPageChange: (pageNo: number) => void;
  onPerPageCountChange: (countPerPage: number) => void;
}

const ENTERPRISE_LOAN_CONFIG_MAP_BY_TAB = {
  COMMERCIAL_OD: {
    loanSelector: (state: ReduxState) => state.loan.commercialOdLoans,
    pageNumberLimitSelector: (state: ReduxState) =>
      state.loan.enterpriseLoanPageNumberLimit.commercialOdLoanPageNumberLimit,
    columns: [
      { field: "_id", label: "Loan ID" },
      {
        field: "enterpriseName",
        label: "Enterprise Name",
        style: { textAlign: "center" },
      },
      {
        field: "createdAt",
        label: "Date Of Loan",
        style: { textAlign: "center" },
      },
      { field: "irpa", label: "IRPA (%)", style: { textAlign: "center" } },
      { field: "pf", label: "PF", style: { textAlign: "center" } },
      { field: "status", label: "Status", style: { textAlign: "center" } },
    ] as TableColumns[],
    getData: (loans: CommercialLoanDetails[]) =>
      loans.map((loan) => ({
        ...loan,
        _id: (
          <LoanIdLink
            path={RoutePaths.ENTERPRISE_LOANS}
            tab="COMMERCIAL_OD"
            id={loan._id}
          />
        ),
        enterpriseName: splitKeyWithSpace(loan.entitySnapshot.snapshot.name),
        createdAt: dateToDDMMYYYY(loan.createdAt),
        status: <LoanStatusBadge status={loan.status} />,
      })),
    fetchAction: getCommercialODLoansBySearch,
  },
  WORKING_CAPITAL_TERM_LOAN: {
    loanSelector: (state: ReduxState) => state.loan.workingCapitalTermLoans,
    pageNumberLimitSelector: (state: ReduxState) =>
      state.loan.enterpriseLoanPageNumberLimit
        .workingCapitalTermLoanPageNumberLimit,
    columns: [
      { field: "_id", label: "Loan ID", style: { textAlign: "center" } },
      {
        field: "enterpriseName",
        label: "Enterprise Name",
        style: { textAlign: "center" },
      },
      { field: "irpa", label: "IRPA (%)", style: { textAlign: "center" } },
      { field: "pf", label: "PF", style: { textAlign: "center" } },
      {
        field: "approvedAmount",
        label: "Approved Amount",
        style: { textAlign: "center" },
      },
      {
        field: "tenureInMonths",
        label: "Tenure In Months",
        style: { textAlign: "center" },
      },
      {
        field: "moratoriumInMonths",
        label: "Moratorium In Months",
        style: { textAlign: "center" },
      },
      { field: "status", label: "Status", style: { textAlign: "center" } },
    ] as TableColumns[],
    getData: (loans: WCTLoanDetail[]) =>
      loans.map((loan) => ({
        ...loan,
        _id: (
          <LoanIdLink
            path={RoutePaths.ENTERPRISE_LOANS}
            tab="WORKING_CAPITAL_TERM_LOAN"
            id={loan._id}
          />
        ),
        enterpriseName: splitKeyWithSpace(loan.entitySnapshot?.snapshot.name),
        status: <LoanStatusBadge status={loan.status as LoanStatusEnum} />,
      })),
    fetchAction: getWCTLoansBySearch,
  },
};

export default function LoanListContainer({
  tab,
  queryState,
  onPageChange,
  onPerPageCountChange,
}: LoanListContainerProps) {
  const storeDispatch = useDispatch();
  const config = ENTERPRISE_LOAN_CONFIG_MAP_BY_TAB[tab];
  const data = useSelector(config.loanSelector);
  const pageNumberLimit = useSelector(config.pageNumberLimitSelector);
  const loadingQueue = useSelector(
    (state: ReduxState) => state.loan.loadingQueue
  );
  const isLoading = isLoadingActive(loadingQueue);

  useEffect(() => {
    dispatch(
      storeDispatch,
      config.fetchAction(clearFormData({ ...queryState }))
    );
  }, [queryState, storeDispatch]);

  const tableData = useMemo(() => {
    if (!data) return [];
    return config.getData(data);
  }, [data, config]);

  if (isLoading) {
    return (
      <div className="d-flex align-items-center">
        <Loader />
      </div>
    );
  }

  if (!tableData || tableData.length === 0) {
    return <p className="text-muted text-center">No Loans Found</p>;
  }

  return (
    <div className="dashboard-container">
      <Table data={tableData} columns={config.columns} />
      <Pagination
        currentPage={queryState.pageNo}
        totalEntries={queryState.numberOfEntitiesPerPage * pageNumberLimit}
        onPageChange={onPageChange}
        onPerPageCountChange={onPerPageCountChange}
      />
    </div>
  );
}
